/**
 * geolibre-worker-protocol — pure request/response plumbing between
 * geolibre-engine.js (main thread) and geolibre.worker.js (Web Worker).
 *
 * Protocol (structured-clone friendly):
 *   request:  { id, tool, args?: string[], files?: { name: string|Uint8Array } }
 *             — `tool` is a geolibre tool id, or a meta op:
 *               '__init__' (compile the WASM), '__list_tools__', '__list_manifests__'.
 *             — string file values are GeoJSON text; the worker encodes them.
 *   response: { id, ok: true, result } | { id, ok: false, error: { message } }
 *
 * The bridge owns the Worker lifecycle:
 *   - LAZY: the Worker is only constructed on the first call() — never at
 *     module import time (Node/vitest have no Worker global).
 *   - TIMEOUT: each call gets a deadline (default 60s). A wedged WASM call
 *     cannot be cancelled cooperatively, so on timeout the bridge TERMINATES
 *     the worker (rejecting every in-flight call) and lets the next call()
 *     construct a fresh one — the WASM recompiles lazily inside it.
 *   - ERROR: a worker 'error' event (script failed to load, uncaught throw)
 *     rejects all in-flight calls and drops the worker for the same reason.
 *
 * No WASM, no DOM — fully unit-testable with a fake Worker
 * (tests/geolibre-worker-protocol.test.js).
 */

export const DEFAULT_CALL_TIMEOUT_MS = 60_000

/** Can this environment run the Worker path at all? Pure decision helper. */
export function workerSupported(scope = globalThis) {
  return typeof scope?.Worker === 'function'
}

/**
 * Create a bridge around a Worker factory.
 *
 * @param {() => Worker} createWorker — constructs the (module) Worker; called
 *   lazily on first use and again after a terminate. May throw (CSP, old
 *   browser) — the pending call() rejects with that error.
 * @param {{ timeoutMs?: number }} [opts] — default per-call timeout.
 * @returns {{
 *   call: (msg: {tool: string, args?: string[], files?: object}, opts?: {timeoutMs?: number}) => Promise<any>,
 *   terminate: () => void,
 *   pendingCount: () => number,
 *   hasWorker: () => boolean,
 * }}
 */
export function createWorkerBridge(createWorker, { timeoutMs = DEFAULT_CALL_TIMEOUT_MS } = {}) {
  let worker = null
  let nextId = 1
  const pending = new Map() // id → { resolve, reject, timer }

  function rejectAll(err) {
    for (const entry of pending.values()) {
      clearTimeout(entry.timer)
      entry.reject(err)
    }
    pending.clear()
  }

  function dropWorker() {
    if (worker) {
      try {
        worker.terminate()
      } catch {
        /* already dead */
      }
      worker = null
    }
  }

  function onMessage(event) {
    const data = event?.data
    if (!data || !pending.has(data.id)) return // stale reply from a pre-timeout call
    const entry = pending.get(data.id)
    pending.delete(data.id)
    clearTimeout(entry.timer)
    if (data.ok) {
      entry.resolve(data.result)
    } else {
      entry.reject(new Error(data.error?.message || 'geolibre worker: unknown error'))
    }
  }

  function onError(event) {
    // Script-level failure — the worker is not trustworthy anymore. Reject
    // everything in flight and recreate on next call.
    const err = new Error(`geolibre worker error: ${event?.message || 'unknown'}`)
    dropWorker()
    rejectAll(err)
  }

  function ensureWorker() {
    if (!worker) {
      worker = createWorker() // may throw — caught by the Promise executor in call()
      worker.onmessage = onMessage
      worker.onerror = onError
    }
    return worker
  }

  function call(msg, { timeoutMs: callTimeoutMs = timeoutMs } = {}) {
    const id = nextId++
    return new Promise((resolve, reject) => {
      const w = ensureWorker() // throw here → promise rejects
      const timer = setTimeout(() => {
        pending.delete(id)
        // A wedged WASM call can't be cancelled — kill the whole worker so the
        // main thread stays responsive; the next call() builds a fresh one.
        dropWorker()
        rejectAll(new Error('geolibre worker terminated: a sibling call timed out'))
        reject(new Error(`geolibre worker call '${msg?.tool}' timed out after ${callTimeoutMs}ms`))
      }, callTimeoutMs)
      pending.set(id, { resolve, reject, timer })
      w.postMessage({ id, ...msg })
    })
  }

  return {
    call,
    terminate() {
      dropWorker()
      rejectAll(new Error('geolibre worker terminated'))
    },
    pendingCount: () => pending.size,
    hasWorker: () => worker !== null,
  }
}
